import { useStreamingOptional } from '@/shared/contexts/StreamingContext';
import { Card } from '@/shared/components/ui/card';
import {
    ChevronDown,
    ChevronUp,
    MessageSquare,
    FolderOpen,
} from 'lucide-react';
import { Loader } from "@/shared/components/ui/loader";
import { cn } from '@/shared/lib/utils';
import { useLocation, useNavigate } from 'react-router-dom';
import { getSessionRoute } from '@/prr';
import { useTranslation } from 'react-i18next';

/**
 * StreamingIndicator - Floating indicator showing active agent processing
 * 
 * Similar to UploadIndicator, this shows when the agent is processing a query
 * in any session, allowing users to track progress even when navigating away.
 */
export function StreamingIndicator() {
    const { t } = useTranslation("shared");
    const streamingContext = useStreamingOptional();
    const location = useLocation();
    const navigate = useNavigate();

    if (!streamingContext) return null;

    const { activeSessions, isMinimized, setIsMinimized } = streamingContext;

    // Don't show if no active streaming
    if (activeSessions.length === 0) return null;

    // Check if user is currently viewing an active session
    const currentSessionId = location.pathname.match(/\/session\/([^/]+)/)?.[1];
    const isViewingActiveSession = currentSessionId &&
        activeSessions.some(s => s.sessionId === currentSessionId);

    // Don't show indicator if user is viewing the active session
    if (isViewingActiveSession) return null;

    const handleNavigateToSession = (projectId: string, sessionId: string) => {
        navigate(getSessionRoute(sessionId, projectId));
    };

    return (
        <Card className={cn(
            "fixed bottom-4 right-4 z-50 shadow-lg transition-all duration-300",
            isMinimized ? "w-auto" : "w-80"
        )}>
            {/* Header */}
            <div
                className="flex items-center justify-between p-3 bg-visagio-teal text-white rounded-t-lg cursor-pointer"
                onClick={() => setIsMinimized(!isMinimized)}
            >
                <div className="flex items-center gap-2">
                    <Loader size="sm" className="text-white" />
                    <span className="font-medium text-sm">
                        {t("streaming.agentProcessing")}
                        {activeSessions.length > 1 && ` (${activeSessions.length})`}
                    </span>
                </div>
                <div className="flex items-center gap-1">
                    {isMinimized ? (
                        <ChevronUp className="h-4 w-4" />
                    ) : (
                        <ChevronDown className="h-4 w-4" />
                    )}
                </div>
            </div>

            {/* Content */}
            {!isMinimized && (
                <div className="max-h-64 overflow-y-auto">
                    {activeSessions.map(session => (
                        <div
                            key={session.sessionId}
                            className="p-3 border-b last:border-b-0 hover:bg-muted/50 cursor-pointer transition-colors"
                            onClick={() => handleNavigateToSession(session.projectId, session.sessionId)}
                        >
                            {/* Project and Session info */}
                            <div className="flex items-center gap-2 mb-2">
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                    <FolderOpen className="h-3 w-3" />
                                    <span className="truncate max-w-[100px]" title={session.projectName}>
                                        {session.projectName}
                                    </span>
                                </div>
                                <span className="text-muted-foreground">•</span>
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                    <MessageSquare className="h-3 w-3" />
                                    <span className="truncate max-w-[100px]" title={session.sessionName}>
                                        {session.sessionName}
                                    </span>
                                </div>
                            </div>

                            {/* User question */}
                            <p className="text-sm font-medium text-foreground mb-2 line-clamp-2">
                                "{session.userQuestion}"
                            </p>

                            {/* Current status */}
                            <div className="flex items-center gap-2">
                                <Loader size="sm" className="shrink-0" />
                                <p className="text-xs text-visagio-teal truncate">
                                    {session.status}
                                </p>
                            </div>

                            {/* Status history (last 3) */}
                            {session.statusHistory.length > 0 && (
                                <div className="mt-2 space-y-1">
                                    {session.statusHistory.slice(-3).map((status, idx) => (
                                        <p key={idx} className="text-xs text-muted-foreground truncate pl-5">
                                            ✓ {status}
                                        </p>
                                    ))}
                                </div>
                            )}

                            {/* Click to navigate hint */}
                            <p className="text-xs text-visagio-teal/70 mt-2">
                                {t("streaming.clickToGoToSession")}
                            </p>
                        </div>
                    ))}
                </div>
            )}
        </Card>
    );
}

export default StreamingIndicator;
